import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, X, Eye, EyeOff } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const API_BASE_URL =
  process.env.REACT_APP_BASE_URL || "http://localhost:3000/api/v1";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      navigate("/");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const url = `${API_BASE_URL}/auth/login`;
      const response = await axios.post(url, { email, password });
      const data = response.data;

      if (data.user && data.user.role === "admin") {
        localStorage.setItem("accessToken", data.accessToken);
        localStorage.setItem("refreshToken", data.refreshToken);
        if (data.user) {
          localStorage.setItem("user", JSON.stringify(data.user));
        }

        navigate("/");
      } else {
        setError("Access Denied: You do not have administrator permissions.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  const closeError = () => {
    setError("");
  };

  return (
    <>
      <style>{`
        /* Your animation styles are great, no changes needed */
        @keyframes pop-in { 0% { transform: scale(0.5) rotate(-15deg); opacity: 0; } 100% { transform: scale(1) rotate(0deg); opacity: 1; } }
        .icon-pop-in { animation: pop-in 0.3s ease-out forwards; }
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .animate-slideIn { animation: slideIn 0.5s ease-out forwards; }
      `}</style>

      <div className="relative flex h-screen w-screen items-center justify-center overflow-hidden bg-black font-sans text-white">
        <div className="absolute -left-[200px] -top-[300px] h-[600px] w-[600px] rounded-full border-2 border-[#8B6726]/30"></div>
        <div className="absolute -bottom-[400px] -right-[300px] h-[800px] w-[800px] rounded-full border-2 border-[#8B6726]/30"></div>

        <div className="relative z-10 flex flex-col items-center gap-10">
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-[#111] shadow-md transition-transform duration-300 ease-in-out hover:scale-105 hover:shadow-lg">
              <img
                src="./SlayLogo.png"
                alt="Slay Logo"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="text-2xl font-bold tracking-[2px] text-[#DE7A2B]">
              SLAY
            </div>
          </div>

          {/* Login Form */}
          <form className="flex w-80 flex-col gap-4" onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-full border border-white/10 bg-white/5 px-5 py-3.5 text-sm text-zinc-400 outline-none transition-all duration-300 ease-in-out focus:border-[#DE7A2B]/30 focus:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
              required
              disabled={loading}
            />
            {/* Password input with visibility toggle */}
            <div className="relative w-full">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-full border border-white/10 bg-white/5 px-5 py-3.5 pr-12 text-sm text-zinc-400 outline-none transition-all duration-300 ease-in-out focus:border-[#DE7A2B]/30 focus:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute top-1/2 right-4 -translate-y-1/2 transform text-zinc-500 transition-colors duration-200 hover:text-zinc-300 focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff key="eye-off" size={20} className="icon-pop-in" />
                ) : (
                  <Eye key="eye-on" size={20} className="icon-pop-in" />
                )}
              </button>
            </div>
            <button
              type="submit"
              className="mx-auto mt-2 w-[90%] rounded-full bg-[#DE7A2B] py-3.5 px-5 text-sm font-semibold text-black transition-all duration-300 ease-in-out hover:bg-[#C96D26] hover:-translate-y-px active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={loading}
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>

        {/* Error Popup */}
        {error && (
          <div className="fixed top-5 right-5 z-[1000] animate-slideIn">
            <div className="flex min-w-[300px] max-w-[400px] items-center gap-4 rounded-xl bg-red-600/95 py-4 px-5 text-white shadow-lg">
              <AlertTriangle size={24} />
              <div className="flex-1 text-sm leading-relaxed">{error}</div>
              <button
                onClick={closeError}
                className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/20"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
